/**
 * PredSpend Data Cache Utility
 *
 * Provides in-memory caching with TTL (time-to-live) specifically for predictive spend
 * data to reduce unnecessary API calls and improve performance for asset replacement planning.
 */

interface CacheItem<T> {
	data: T;
	timestamp: number;
	ttl: number; // Time to live in milliseconds
}

interface PredSpendCacheConfig {
	ttl: number; // Default TTL in milliseconds
}

class PredSpendCache {
	private cache = new Map<string, CacheItem<any>>();
	private readonly defaultTTL: number;

	constructor(config: PredSpendCacheConfig = { ttl: 10 * 60 * 1000 }) {
		// Default 10 minutes for predspend
		this.defaultTTL = config.ttl;
	}

	/**
	 * Generate cache key based on organization ID and optional strategy/filter params
	 */
	private generateKey(
		organizationId: number,
		prefix: string,
		params?: {
			yearView?: number;
			replacementStrategy?: string;
			filters?: Record<string, any>;
		},
	): string {
		const baseKey = `predspend_${prefix}_${organizationId}`;
		if (!params) return baseKey;

		// Sort params for consistent key generation
		const sortedParams = Object.keys(params)
			.sort()
			.map(
				(key) => `${key}:${JSON.stringify(params[key as keyof typeof params])}`,
			)
			.join("|");

		return `${baseKey}_${sortedParams}`;
	}

	/**
	 * Check if cached item is still valid
	 */
	private isValid<T>(item: CacheItem<T>): boolean {
		return Date.now() - item.timestamp < item.ttl;
	}

	/**
	 * Get cached data if valid
	 */
	get<T>(
		organizationId: number,
		prefix: string,
		params?: {
			yearView?: number;
			replacementStrategy?: string;
			filters?: Record<string, any>;
		},
	): T | null {
		const key = this.generateKey(organizationId, prefix, params);
		const item = this.cache.get(key) as CacheItem<T> | undefined;

		if (!item) {
			return null;
		}

		if (this.isValid(item)) {
			return item.data;
		}

		// Remove expired item
		this.cache.delete(key);
		return null;
	}

	/**
	 * Set cached data with optional custom TTL
	 */
	set<T>(
		organizationId: number,
		prefix: string,
		data: T,
		params?: {
			yearView?: number;
			replacementStrategy?: string;
			filters?: Record<string, any>;
		},
		customTTL?: number,
	): void {
		const key = this.generateKey(organizationId, prefix, params);
		const ttl = customTTL || this.defaultTTL;

		this.cache.set(key, {
			data,
			timestamp: Date.now(),
			ttl,
		});
	}

	/**
	 * Invalidate specific cache entry
	 */
	invalidate(
		organizationId: number,
		prefix: string,
		params?: {
			yearView?: number;
			replacementStrategy?: string;
			filters?: Record<string, any>;
		},
	): void {
		const key = this.generateKey(organizationId, prefix, params);
		this.cache.delete(key);
	}

	/**
	 * Invalidate all cache entries for an organization
	 */
	invalidateOrganization(organizationId: number): void {
		const keysToDelete: string[] = [];

		for (const key of this.cache.keys()) {
			if (key.includes(`_${organizationId}`)) {
				keysToDelete.push(key);
			}
		}

		keysToDelete.forEach((key) => this.cache.delete(key));
	}

	/**
	 * Invalidate all cache entries with specific strategy
	 */
	invalidateStrategy(organizationId: number, strategy: string): void {
		const keysToDelete: string[] = [];

		for (const key of this.cache.keys()) {
			if (
				key.includes(`_${organizationId}`) &&
				key.includes(`replacementStrategy:${JSON.stringify(strategy)}`)
			) {
				keysToDelete.push(key);
			}
		}

		keysToDelete.forEach((key) => this.cache.delete(key));
	}

	/**
	 * Clear all cached data
	 */
	clear(): void {
		this.cache.clear();
	}

	/**
	 * Get cache statistics
	 */
	getStats(): {
		totalEntries: number;
		validEntries: number;
		expiredEntries: number;
	} {
		let validEntries = 0;
		let expiredEntries = 0;

		for (const item of this.cache.values()) {
			if (this.isValid(item)) {
				validEntries++;
			} else {
				expiredEntries++;
			}
		}

		return {
			totalEntries: this.cache.size,
			validEntries,
			expiredEntries,
		};
	}

	/**
	 * Clean up expired entries
	 */
	cleanup(): void {
		const keysToDelete: string[] = [];

		for (const [key, item] of this.cache.entries()) {
			if (!this.isValid(item)) {
				keysToDelete.push(key);
			}
		}

		keysToDelete.forEach((key) => this.cache.delete(key));
	}
}

// Create singleton instance with longer TTL for predspend data
const predspendCache = new PredSpendCache({
	ttl: 10 * 60 * 1000, // 10 minutes default TTL (longer than dashboard)
});

// Auto-cleanup expired entries every 15 minutes
setInterval(
	() => {
		predspendCache.cleanup();
	},
	15 * 60 * 1000,
);

// Cache key prefixes specific to predspend functionality
export const PREDSPEND_CACHE_KEYS = {
	ANALYTICS: "analytics",
	CHART_DATA: "chart_data",
	PROCESSED_DATA: "processed_data",
	EXPORT_DATA: "export_data",
} as const;

export { predspendCache };
export type { PredSpendCacheConfig };
